import { useCallback, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { adminContent } from '../../services/api';
import { MediaAsset, MediaAssetType } from 'types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Spinner } from '../ui/spinner';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PaginationControls } from '../ui/pagination';
import { cn } from 'lib/utils';

type MediaAssetPickerProps = {
  value: MediaAsset | null;
  onChange: (asset: MediaAsset | null) => void;
  mediaTypes?: MediaAssetType[];
  label?: string;
  description?: string;
  disabled?: boolean;
};

type PaginatedResponse<T> = {
  results?: T[];
  next?: string | null;
  previous?: string | null;
  count?: number;
  total?: number;
};

type MediaTypeFilter = MediaAssetType | 'all';

const MEDIA_FILTER_OPTIONS: Array<{ value: MediaTypeFilter; label: string }> = [
  { value: 'all', label: 'All media' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
  { value: 'audio', label: 'Audio' },
  { value: 'pdf', label: 'PDFs' },
];

const DEFAULT_PAGE_SIZE = 20;

const extractItems = <T,>(payload: unknown): { items: T[]; count: number } => {
  if (Array.isArray(payload)) {
    return { items: payload as T[], count: payload.length };
  }

  if (payload && typeof payload === 'object') {
    const cast = payload as PaginatedResponse<T>;
    const items = Array.isArray(cast.results) ? cast.results : [];
    const count =
      typeof cast.count === 'number'
        ? cast.count
        : typeof cast.total === 'number'
        ? cast.total
        : items.length;
    return { items, count };
  }

  return { items: [], count: 0 };
};

const formatMediaLabel = (asset: MediaAsset) => {
  if (asset.alt_text) {
    return asset.alt_text;
  }
  const key = asset.storage_key ?? '';
  const parts = key.split('/');
  return parts[parts.length - 1] || key || asset.id;
};

export const MediaAssetPicker: React.FC<MediaAssetPickerProps> = ({
  value,
  onChange,
  mediaTypes,
  label,
  description,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dialogMediaType, setDialogMediaType] = useState<MediaTypeFilter>('all');

  const availableFilterOptions = useMemo(() => {
    if (!mediaTypes || mediaTypes.length === 0) {
      return MEDIA_FILTER_OPTIONS;
    }

    const unique = Array.from(new Set(mediaTypes));
    const filtered = MEDIA_FILTER_OPTIONS.filter((option) => {
      if (option.value === 'all') {
        return unique.length > 1;
      }
      return unique.includes(option.value);
    });

    return filtered.length > 0 ? filtered : MEDIA_FILTER_OPTIONS;
  }, [mediaTypes]);

  const loadAssets = useCallback(
    async (
      nextPage: number,
      options?: { search?: string; mediaType?: MediaTypeFilter }
    ) => {
      setLoading(true);
      setError(null);

      const searchValue =
        options?.search !== undefined ? options.search : appliedSearch;
      const resolvedMediaType =
        mediaTypes && mediaTypes.length > 0
          ? mediaTypes.join(',')
          : (options?.mediaType ?? dialogMediaType) !== 'all'
          ? options?.mediaType ?? dialogMediaType
          : undefined;

      try {
        const response = await adminContent.mediaAssets.list({
          page: nextPage,
          page_size: DEFAULT_PAGE_SIZE,
          search: searchValue || undefined,
          media_type: resolvedMediaType,
        });

        const { items, count } = extractItems<MediaAsset>(response.data);
        const total = typeof count === 'number' ? count : items.length;
        const startIndex = Math.max(0, (nextPage - 1) * DEFAULT_PAGE_SIZE);
        const endIndex = startIndex + DEFAULT_PAGE_SIZE;
        const needsClientPaging = items.length > DEFAULT_PAGE_SIZE;
        const visibleItems = needsClientPaging ? items.slice(startIndex, endIndex) : items;

        setAssets(visibleItems);
        setTotalCount(total);
        setPage(nextPage);
        setAppliedSearch(searchValue);
      } catch (err) {
        console.error('Failed to load media assets', err);
        setError('Unable to load media assets.');
        setAssets([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [appliedSearch, dialogMediaType, mediaTypes],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setOpen(false);
        setSearchTerm('');
        setAppliedSearch('');
        setDialogMediaType('all');
        setAssets([]);
        setTotalCount(0);
        return;
      }

      const initialType: MediaTypeFilter =
        mediaTypes && mediaTypes.length === 1 ? mediaTypes[0] : 'all';

      setOpen(true);
      setSearchTerm('');
      setAppliedSearch('');
      setPage(1);
      setDialogMediaType(initialType);
      void loadAssets(1, { search: '', mediaType: initialType });
    },
    [loadAssets, mediaTypes],
  );

  const handleSubmitSearch = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const normalized = searchTerm.trim();
      if (normalized === appliedSearch) {
        return;
      }
      setPage(1);
      void loadAssets(1, { search: normalized });
    },
    [appliedSearch, loadAssets, searchTerm],
  );

  const handleFilterChange = useCallback(
    (nextType: MediaTypeFilter) => {
      setDialogMediaType(nextType);
      setPage(1);
      void loadAssets(1, { search: appliedSearch, mediaType: nextType });
    },
    [appliedSearch, loadAssets],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      if (nextPage === page) {
        return;
      }
      void loadAssets(nextPage);
    },
    [loadAssets, page],
  );

  const handleSelect = useCallback(
    (asset: MediaAsset) => {
      onChange(asset);
      setOpen(false);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const showPagination = totalCount > DEFAULT_PAGE_SIZE;

  return (
    <>
      <Card className="w-full" variant='white'>
        {(label || description) && (
          <CardHeader className="space-y-1">
            {label ? (
              <CardTitle className="text-sm font-medium text-foreground">{label}</CardTitle>
            ) : null}
            {description ? (
              <p className="text-sm text-muted-foreground">{description}</p>
            ) : null}
          </CardHeader>
        )}
        <CardContent className="flex w-full flex-col items-center gap-3 text-center">
          <div className="flex w-full max-w-full flex-wrap items-center justify-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleOpenChange(true)}
              disabled={disabled}
            >
              {value ? 'Change asset' : 'Select asset'}
            </Button>
            {value ? (
              <div className="flex min-w-0 flex-1 max-w-full items-start gap-2 overflow-hidden rounded-md border border-border/80 bg-muted/40 px-3 py-2 text-sm text-left">
                <div className="flex min-w-0 flex-col">
                  <span className="font-medium text-foreground break-all">
                    {formatMediaLabel(value)}
                  </span>
                  <span className="text-xs text-muted-foreground break-all">
                    {value.media_type ?? 'unknown'} - {value.storage_key}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={handleClear}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Select a media asset</DialogTitle>
            <DialogDescription>
              Browse existing assets stored in the CDN and choose one to attach.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <form onSubmit={handleSubmitSearch} className="flex flex-col gap-2">
              <Label htmlFor="media-asset-picker-search" className="text-xs text-muted-foreground">
                Search by storage key or alt text
              </Label>
              <div className="flex gap-2">
                <Input
                  id="media-asset-picker-search"
                  autoFocus
                  placeholder="Search assets"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <Button type="submit" variant="secondary" disabled={loading}>
                  {loading && assets.length === 0 ? <Spinner size="sm" /> : 'Search'}
                </Button>
              </div>
            </form>

            {availableFilterOptions.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs text-muted-foreground">Media type</Label>
                <Select
                  value={dialogMediaType}
                  onValueChange={(value) => handleFilterChange(value as MediaTypeFilter)}
                  disabled={loading || availableFilterOptions.length === 1}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="max-h-[360px] overflow-y-auto rounded-lg border border-border/60">
              {loading && assets.length === 0 ? (
                <div className="flex h-40 items-center justify-center">
                  <Spinner />
                </div>
              ) : error ? (
                <div className="p-4 text-sm text-destructive">{error}</div>
              ) : assets.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No assets found.</div>
              ) : (
                <div className="divide-y divide-border/60">
                  {assets.map((asset) => {
                    const isSelected = value?.id === asset.id;
                    return (
                      <button
                        key={asset.id}
                        type="button"
                        className={cn(
                          'flex w-full max-w-full items-start gap-3 overflow-hidden px-4 py-3 text-left transition-colors',
                          isSelected ? 'bg-primary/10' : 'hover:bg-muted/60',
                        )}
                        onClick={() => handleSelect(asset)}
                      >
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="font-medium text-foreground break-all">
                            {formatMediaLabel(asset)}
                          </span>
                          <span className="text-xs text-muted-foreground break-all">
                            {asset.storage_key}
                          </span>
                        </div>
                        <Badge variant="outline" className="shrink-0 text-xs uppercase">
                          {asset.media_type}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {showPagination ? (
              <PaginationControls
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={totalCount}
                onPageChange={handlePageChange}
              />
            ) : null}
          </div>

          <DialogFooter className="mt-3 flex justify-end">
            <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MediaAssetPicker;
